import { useMemo, useState, type ReactNode } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { keepPreviousData } from '@tanstack/react-query'
import { getCoreRowModel, useReactTable, type PaginationState } from '@tanstack/react-table'
import toPairs from 'lodash/toPairs'
import values from 'lodash/values'
import isFunction from 'lodash/isFunction'
import map from 'lodash/map'
import sumBy from 'lodash/sumBy'
import toInteger from 'lodash/toInteger'
import toString from 'lodash/toString'
import { CheckCircle2, Info, Paperclip, XCircle } from 'lucide-react'
import { ROUTE_CUSTOMERS, ROUTE_ORDER_EVENTS, ROUTE_ORDERS, ROUTE_RECEIVABLES } from '@klear/constants'
import { KlearDate } from '@klear/date'
import type { AwaitingQuoteRow } from '@klear/invoice/awaiting-quote'
import {
  FUND_READINESS_TABS,
  type FundReadinessCounts,
  type FundReadinessRow,
  type FundReadinessTab
} from '@klear/invoice-offer/fund-readiness'
import type { InvoiceOfferFundReadinessSort } from '@klear/order-event/invoice-offer.input.wire'
import { paymentStatusLabel } from '@klear/payment/presentation'
import { Combobox, type ComboboxOption } from '@klear/ui/components/combobox'
import { DataTablePagination } from '@klear/ui/components/data-table-pagination'
import { Skeleton } from '@klear/ui/components/skeleton'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@klear/ui/components/table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@klear/ui/components/tooltip'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'
import { useOrderEventDocuments } from '@klear/ui/hooks/use-order-event-documents'
import { useTraders } from '@klear/ui/hooks/use-traders'
import { cn } from '@klear/ui/lib/utils'
import { trpc } from '@klear/ui/lib/trpc'
import {
  FUND_CHECK_LABELS,
  STATUS_LABELS,
  creditCell,
  daysSince,
  formatDate,
  formatMoney,
  plural,
  statusCount
} from '@/app/receivables/fund-checks'
import { StatusPill } from '@/app/receivables/status-pill'
import { FloatingHeader, useScrolledOut, useScrollMainToTop } from '@/app/receivables/floating-header'

const PAGE_SIZE = 50

const TABS: Record<FundReadinessTab, { label: string; empty: string }> = {
  action: {
    label: 'Needs action',
    empty: 'Nothing needs action. Invoices awaiting a quote and accepted offers show here until they are funded.'
  },
  quote: { label: 'Quotes', empty: 'No quotes right now. New offers show here as soon as Klear prices an invoice.' },
  funded: { label: 'Funded', empty: 'No funded offers yet.' },
  closed: { label: 'Closed', empty: 'No completed or voided offers yet.' },
  all: { label: 'All', empty: 'No offers yet.' }
}

const AWAITING_QUOTE_TABS: readonly FundReadinessTab[] = ['action', 'all']

function withAwaitingQuotes(
  counts: FundReadinessCounts | undefined,
  awaiting: number
): FundReadinessCounts | undefined {
  if (counts == null) return undefined
  return { ...counts, action: counts.action + awaiting, all: counts.all + awaiting }
}

const isReceivablesTab = (value: string | null): value is FundReadinessTab =>
  FUND_READINESS_TABS.some((one) => one === value)

const SORTS: Record<string, InvoiceOfferFundReadinessSort> = {
  Trader: 'trader',
  Customer: 'customer',
  Invoice: 'invoice',
  Credit: 'credit',
  SPV: 'spv',
  'Purchase price': 'price',
  Fees: 'fees',
  Status: 'status',
  Due: 'due',
  Accepted: 'date'
}

const isSort = (value: string | null): value is InvoiceOfferFundReadinessSort =>
  values(SORTS).some((one) => one === value)

type TooltipItem = { key: string; label: string; reason: string | null }

function statusDetail(row: FundReadinessRow): { heading: string; items: TooltipItem[]; info: boolean } | null {
  const payment = row.disbursement
  if (payment != null && (row.status === 'funding' || row.status === 'fundingFailed')) {
    const failed = row.status === 'fundingFailed'
    return {
      heading: failed ? 'The advance payment failed' : 'The advance is being sent',
      items: [
        {
          key: 'payment',
          label: `${payment.paymentIdDisplay ?? 'Payment'} · ${paymentStatusLabel(payment.status)}`,
          reason: [failed ? payment.error : null, row.advancePaymentNextStep].filter(Boolean).join(' ')
        }
      ],
      info: !failed
    }
  }
  if (row.recovery != null) {
    return {
      heading: `Funded ${formatDate(row.fundedAt)}`,
      items: [
        {
          key: 'due',
          label: `Recovery due ${formatDate(row.recovery?.dueDate)}`,
          reason: `${formatMoney(row.recovery?.remaining)} outstanding`
        },
        {
          key: 'fees',
          label: 'Late fees',
          reason: row.recovery?.extensionFees ? `${formatMoney(row.recovery.extensionFees)} accrued` : 'None'
        }
      ],
      info: true
    }
  }
  if (!row.blockedFromFunding) return null
  return {
    heading: `${plural(row.fundBlockers.length, 'check')} blocking funding`,
    items: row.fundBlockers.map((check) => ({
      key: check.key,
      label: FUND_CHECK_LABELS[check.key],
      reason: check.reason
    })),
    info: false
  }
}

function StatusCell({ row }: { row: FundReadinessRow }) {
  const { label, tone } = STATUS_LABELS[row.status]
  const count = statusCount(row)
  const text = count == null ? label : `${label} · ${count}`
  const detail = statusDetail(row)
  if (detail == null) return <StatusPill tone={tone}>{text}</StatusPill>
  const { heading, items, info } = detail
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <StatusPill tone={tone} tabIndex={0} className="cursor-help" onClick={(event) => event.stopPropagation()}>
            {text}
          </StatusPill>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          align="start"
          collisionPadding={8}
          className="grid w-[340px] gap-2 border bg-background px-4 py-3 text-sm text-foreground shadow-lg"
        >
          <span className="text-xs text-muted-foreground">{heading}</span>
          <ul className="grid gap-2">
            {items.map((item) => (
              <li key={item.key} className="grid grid-cols-[16px_minmax(0,1fr)] gap-2">
                {info ? (
                  <Info className="mt-0.5 h-4 w-4 text-muted-foreground" aria-hidden />
                ) : (
                  <XCircle className="mt-0.5 h-4 w-4 text-danger" aria-hidden />
                )}
                <span>
                  <b className="block font-medium">{item.label}</b>
                  <span className="block whitespace-normal text-xs text-muted-foreground">{item.reason}</span>
                </span>
              </li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function AwaitingQuoteStatus({ reason }: { reason: string | null }) {
  return (
    <div className="grid justify-items-start gap-1">
      <StatusPill tone="neutral">Awaiting quote</StatusPill>
      <span className="max-w-64 whitespace-normal text-xs text-muted-foreground">
        {reason == null ? 'Not priced yet. The auto-pricer has not recorded a reason.' : `Not priced yet: ${reason}`}
      </span>
    </div>
  )
}

function FilesCell({ groups }: { groups: { label: string; names: string[] }[] }) {
  const count = sumBy(groups, (group) => group.names.length)
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            onClick={(event) => event.stopPropagation()}
            className={cn(
              'flex w-fit cursor-help items-center gap-1 text-xs',
              count > 0 ? 'text-foreground' : 'text-muted-foreground'
            )}
          >
            <Paperclip className="h-3 w-3" aria-hidden />
            {count > 0 ? plural(count, 'file') : 'No files'}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          align="start"
          collisionPadding={8}
          className="grid w-[340px] gap-2 border bg-background px-4 py-3 text-sm text-foreground shadow-lg"
        >
          <ul className="grid gap-2">
            {groups.map((group) => (
              <li key={group.label} className="grid grid-cols-[16px_minmax(0,1fr)] gap-2">
                {group.names.length > 0 ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-success" aria-hidden />
                ) : (
                  <XCircle className="mt-0.5 h-4 w-4 text-danger" aria-hidden />
                )}
                <span>
                  <b className="block font-medium">{group.label}</b>
                  <span className="block whitespace-normal break-words text-xs text-muted-foreground">
                    {group.names.length > 0 ? group.names.join(', ') : 'No files'}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function FilterTabs({
  counts,
  active,
  onPick
}: {
  counts: FundReadinessCounts | undefined
  active: FundReadinessTab
  onPick: (id: FundReadinessTab) => void
}) {
  return (
    <div
      role="tablist"
      aria-label="Filter offers"
      className="inline-flex w-fit max-w-full overflow-x-auto rounded-lg bg-muted p-1"
    >
      {FUND_READINESS_TABS.map((id) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={id === active}
          onClick={() => onPick(id)}
          className={cn(
            'inline-flex h-7 items-center gap-2 whitespace-nowrap rounded-md px-3 text-sm font-medium text-muted-foreground',
            id === active && 'bg-background text-foreground'
          )}
        >
          {TABS[id].label}
          {counts == null ? null : (
            <span className="inline-grid h-5 min-w-5 place-items-center rounded-full bg-border px-1.5 text-xs tabular-nums">
              {counts[id]}
            </span>
          )}
        </button>
      ))}
    </div>
  )
}

function RecordLink({ to, children }: { to: string | null; children: string | null }) {
  if (to == null || children == null) return <>{children ?? '—'}</>
  return (
    <Link to={to} onClick={(event) => event.stopPropagation()} className="underline-offset-4 hover:underline">
      {children}
    </Link>
  )
}

function TraderPicker({ value, onChange }: { value: number | undefined; onChange: (id: string) => void }) {
  const { traders = [] } = useTraders()
  const options = useMemo<ComboboxOption[]>(
    () => traders.map((trader) => ({ value: toString(trader.id), label: trader.name ?? toString(trader.id) })),
    [traders]
  )
  return (
    <Combobox
      options={options}
      value={value == null ? '' : toString(value)}
      onChange={onChange}
      triggerLabel="All traders"
      placeholder="Search traders..."
      className="w-64"
    />
  )
}

export default function Receivables() {
  useDocumentTitle('Receivables')
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab')
  const sortParam = params.get('sort')
  const sort = isSort(sortParam) ? sortParam : 'status'
  const dir = params.get('dir') === 'desc' ? 'desc' : 'asc'
  const traderParam = toInteger(params.get('trader'))
  const traderId = traderParam > 0 ? traderParam : undefined
  const active: FundReadinessTab = isReceivablesTab(tab) ? tab : 'action'
  const setParam = (changes: Record<string, string | null>) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        for (const [key, value] of toPairs(changes)) {
          if (value == null || value === '') next.delete(key)
          else next.set(key, value)
        }
        return next
      },
      { replace: true }
    )
  const [headerRef, collapsed] = useScrolledOut()
  const view = [active, sort, dir, traderId].join('|')
  const [paging, setPaging] = useState({ view, pageIndex: 0, pageSize: PAGE_SIZE })
  const pagination: PaginationState = {
    pageIndex: paging.view === view ? paging.pageIndex : 0,
    pageSize: paging.pageSize
  }
  useScrollMainToTop(pagination.pageIndex)
  const awaitingQuote = trpc.invoice.awaitingQuote.useQuery({ traderId }, { placeholderData: keepPreviousData })
  const allAwaiting: AwaitingQuoteRow[] = AWAITING_QUOTE_TABS.includes(active) ? (awaitingQuote.data ?? []) : []
  const start = pagination.pageIndex * pagination.pageSize
  const awaiting = allAwaiting.slice(start, start + pagination.pageSize)
  const offerLimit = pagination.pageSize - awaiting.length
  const { documentsFor, isFetching: loadingDocuments } = useOrderEventDocuments(
    awaiting.flatMap((row) => [row.invoiceId, row.purchaseOrderId])
  )
  const { data, isError, refetch } = trpc.invoiceOffer.fundReadiness.useQuery(
    {
      tab: active,
      traderId,
      sort,
      dir,
      offset: Math.max(0, start - allAwaiting.length),
      limit: Math.max(1, offerLimit)
    },
    {
      placeholderData: keepPreviousData,
      enabled: !AWAITING_QUOTE_TABS.includes(active) || (awaitingQuote.data != null && !awaitingQuote.isPlaceholderData)
    }
  )
  const counts = withAwaitingQuotes(data?.counts, awaitingQuote.data?.length ?? 0)
  const rows = (data?.rows ?? []).slice(0, offerLimit)
  const total = allAwaiting.length + (data?.total ?? 0)
  const table = useReactTable({
    data: [],
    columns: [],
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: total,
    state: { pagination },
    onPaginationChange: (updater) => setPaging({ view, ...(isFunction(updater) ? updater(pagination) : updater) })
  })
  const loaded = data != null && !isError && (!AWAITING_QUOTE_TABS.includes(active) || awaitingQuote.data != null)
  const summary = loaded ? data.summary : null
  const pickTab = (id: FundReadinessTab) => setParam({ tab: id === 'action' ? null : id })
  const pickTrader = (id: string) => setParam({ trader: id })

  const header = (label: string, { numeric = false, wide = false, wider = false } = {}) => {
    const key = SORTS[label]
    const isActive = key != null && sort === key
    let text = label
    if (label === 'Accepted' && (active === 'funded' || active === 'closed')) text = 'Funded'
    else if (label === 'Accepted' && active === 'all') text = 'Accepted / funded'
    let arrow = '↕'
    if (isActive) arrow = dir === 'asc' ? '↑' : '↓'
    let ariaSort: 'ascending' | 'descending' | 'none' = 'none'
    if (isActive) ariaSort = dir === 'asc' ? 'ascending' : 'descending'
    return (
      <TableHead
        key={label}
        className={cn(
          'h-10 whitespace-nowrap px-3 font-medium',
          numeric && 'text-right',
          wide && 'hidden @6xl:table-cell',
          wider && 'hidden @7xl:table-cell'
        )}
        aria-sort={key == null ? undefined : ariaSort}
      >
        {key == null ? (
          text
        ) : (
          <button
            type="button"
            onClick={() =>
              setParam({ sort: key === 'status' ? null : key, dir: isActive && dir === 'asc' ? 'desc' : null })
            }
            className={cn(
              'inline-flex items-center gap-1 hover:text-foreground',
              isActive && 'text-foreground',
              numeric && 'flex-row-reverse'
            )}
          >
            {text}
            <span aria-hidden className={cn('text-xs', isActive ? 'opacity-100' : 'opacity-50')}>
              {arrow}
            </span>
          </button>
        )}
      </TableHead>
    )
  }

  const message = (text: ReactNode) => (
    <TableRow>
      <TableCell colSpan={12} className="px-3 py-8 text-center text-muted-foreground">
        {text}
      </TableCell>
    </TableRow>
  )

  return (
    <div className="p-8">
      <FloatingHeader visible={collapsed}>
        <div className="flex min-w-0 items-center gap-3">
          <h2 className="text-lg font-bold tracking-tight">Receivables</h2>
          {summary == null ? null : (
            <span className="hidden truncate text-muted-foreground xl:inline">
              {summary.blocked} blocked · {summary.ready} ready
            </span>
          )}
          <TraderPicker value={traderId} onChange={pickTrader} />
        </div>
        <FilterTabs counts={counts} active={active} onPick={pickTab} />
      </FloatingHeader>

      <div ref={headerRef} className="grid gap-8 pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="grid gap-1">
            <h2 className="text-2xl font-bold tracking-tight">Receivables</h2>
            <p className="text-muted-foreground">
              Trader invoices, from awaiting a quote until funded and recovered.
              {summary == null
                ? null
                : ` ${plural(summary.blocked, 'accepted offer')} blocked from funding · ${summary.ready} ready.`}
            </p>
          </div>
          <TraderPicker value={traderId} onChange={pickTrader} />
        </div>
        <FilterTabs counts={counts} active={active} onPick={pickTab} />
      </div>
      <div className="@container grid min-w-0 grid-cols-[minmax(0,1fr)] gap-3">
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full caption-bottom text-sm tabular-nums">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {header('')}
                {header('Trader')}
                {header('Customer')}
                {header('Invoice')}
                {header('Credit', { wide: true })}
                {header('SPV', { wide: true })}
                {header('Purchase price', { numeric: true })}
                {header('Fees', { numeric: true, wider: true })}
                {header('Status')}
                {header('Due')}
                {header('Accepted')}
                <TableHead className="w-[1%]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data == null && !isError ? (
                <TableRow>
                  <TableCell colSpan={12} className="p-3">
                    <Skeleton className="h-24 w-full" />
                  </TableCell>
                </TableRow>
              ) : null}
              {isError
                ? message(
                    <>
                      Could not load offers.{' '}
                      <button type="button" className="underline" onClick={() => void refetch()}>
                        Try again
                      </button>
                    </>
                  )
                : null}
              {loaded && rows.length === 0 && awaiting.length === 0
                ? message(traderId == null ? TABS[active].empty : 'No offers for this trader.')
                : null}
              {awaiting.map((row) => {
                const toCustomer = row.blockedOnCustomerCredit && row.customerId != null
                const open = () =>
                  void navigate(
                    toCustomer
                      ? `/${ROUTE_CUSTOMERS}/${row.customerId}`
                      : `/${ROUTE_ORDER_EVENTS}/${row.invoiceIdDisplay}`
                  )
                const invoicesIn = KlearDate.daysBetween(KlearDate.today(), row.invoiceDate)
                const fileGroups = [
                  { label: 'Invoice', names: map(documentsFor(row.invoiceId), 'name') },
                  { label: 'Purchase order', names: map(documentsFor(row.purchaseOrderId), 'name') }
                ]
                return (
                  <TableRow
                    key={`invoice-${row.invoiceId}`}
                    tabIndex={0}
                    onClick={open}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && event.target === event.currentTarget) open()
                    }}
                    className="group cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
                  >
                    <TableCell className="whitespace-nowrap p-3 text-muted-foreground">
                      No offer
                      {fileGroups.some((group) => group.names.length > 0) || !loadingDocuments ? (
                        <FilesCell groups={fileGroups} />
                      ) : null}
                    </TableCell>
                    <TableCell className="min-w-36 p-3">{row.traderName}</TableCell>
                    <TableCell className="p-3">
                      <RecordLink to={row.customerId == null ? null : `/${ROUTE_CUSTOMERS}/${row.customerId}`}>
                        {row.customerName}
                      </RecordLink>
                    </TableCell>
                    <TableCell className="whitespace-nowrap p-3">
                      <RecordLink to={`/${ROUTE_ORDER_EVENTS}/${row.invoiceIdDisplay}`}>
                        {row.invoiceIdDisplay}
                      </RecordLink>
                      <span className="block text-xs text-muted-foreground">
                        <RecordLink to={`/${ROUTE_ORDERS}/${row.orderIdDisplay}`}>{row.orderIdDisplay}</RecordLink>
                      </span>
                    </TableCell>
                    <TableCell className="hidden p-3 text-muted-foreground @6xl:table-cell">—</TableCell>
                    <TableCell className="hidden p-3 text-muted-foreground @6xl:table-cell">—</TableCell>
                    <TableCell className="whitespace-nowrap p-3 text-right">
                      {formatMoney(row.amount)}
                      <span className="block text-xs text-muted-foreground">invoice</span>
                    </TableCell>
                    <TableCell className="hidden p-3 text-right text-muted-foreground @7xl:table-cell">—</TableCell>
                    <TableCell className="p-3">
                      <AwaitingQuoteStatus reason={row.reason} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap p-3">{formatDate(row.dueDate)}</TableCell>
                    <TableCell className="whitespace-nowrap p-3 text-muted-foreground">
                      {invoicesIn > 0 ? `Invoices in ${invoicesIn}d` : `Invoiced ${daysSince(row.invoiceDate)}`}
                    </TableCell>
                    <TableCell className="whitespace-nowrap p-3 text-right text-xs text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                      {toCustomer ? 'Customer credit →' : 'Invoice →'}
                    </TableCell>
                  </TableRow>
                )
              })}
              {rows.map((row) => {
                const credit = creditCell(row.credit)
                const open = () => void navigate(`/${ROUTE_RECEIVABLES}/${row.offerId}`)
                return (
                  <TableRow
                    key={row.offerId}
                    tabIndex={0}
                    onClick={open}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && event.target === event.currentTarget) open()
                    }}
                    className="group cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
                  >
                    <TableCell className="whitespace-nowrap p-3 font-medium">{row.offerIdDisplay}</TableCell>
                    <TableCell className="min-w-36 p-3">{row.traderName}</TableCell>
                    <TableCell className="p-3">{row.customerName}</TableCell>
                    <TableCell className="whitespace-nowrap p-3">
                      <RecordLink
                        to={row.invoiceIdDisplay == null ? null : `/${ROUTE_ORDER_EVENTS}/${row.invoiceIdDisplay}`}
                      >
                        {row.invoiceIdDisplay}
                      </RecordLink>
                      <span className="block text-xs text-muted-foreground">
                        <RecordLink to={row.orderIdDisplay == null ? null : `/${ROUTE_ORDERS}/${row.orderIdDisplay}`}>
                          {row.orderIdDisplay}
                        </RecordLink>
                      </span>
                    </TableCell>
                    <TableCell className="hidden p-3 @6xl:table-cell">
                      {credit.label}
                      {credit.detail ? (
                        <span className="block text-xs text-muted-foreground">{credit.detail}</span>
                      ) : null}
                    </TableCell>
                    <TableCell className="hidden whitespace-nowrap p-3 @6xl:table-cell">
                      {row.fundingSpv ?? '—'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap p-3 text-right">{formatMoney(row.purchasePrice)}</TableCell>
                    <TableCell className="hidden whitespace-nowrap p-3 text-right @7xl:table-cell">
                      {formatMoney(row.feeTotal)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap p-3">
                      <StatusCell row={row} />
                    </TableCell>
                    <TableCell className={cn('whitespace-nowrap p-3', row.status === 'overdue' && 'text-warning')}>
                      {formatDate(row.dueDate)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap p-3">
                      {row.fundedAt != null ? formatDate(row.fundedAt) : (daysSince(row.acceptedAt) ?? 'Not yet')}
                    </TableCell>
                    <TableCell className="whitespace-nowrap p-3 text-right text-xs text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                      View →
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </table>
        </div>
        <DataTablePagination table={table} />
      </div>
    </div>
  )
}
